import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useAdminAddNote,
  useAdminRfq,
  useAdminUpdateStatus,
  type AdminAssignableStatus,
  type AdminCustomer,
  type AdminNote,
} from "../admin/api.js";
import { serviceCategoryLabels, urgencyLabels } from "../rfqs/api.js";
import { AdminCard, StatusBadge } from "./AdminShell.js";

const ASSIGNABLE: { value: AdminAssignableStatus; label: string }[] = [
  { value: "IN_REVIEW", label: "Mark as In review" },
  { value: "CLOSED", label: "Mark as Closed" },
];

export function AdminRfqDetail() {
  const params = useParams<{ customerSub: string; id: string }>();
  const customerSub = params.customerSub ?? "";
  const id = params.id ?? "";
  const query = useAdminRfq(customerSub, id);
  const mutation = useAdminUpdateStatus(customerSub, id);
  const addNote = useAdminAddNote(customerSub, id);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");

  if (query.isLoading) {
    return <p className="text-sm text-slate-500">Loading…</p>;
  }
  if (query.isError || !query.data) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-rose-700">
          {query.error instanceof Error ? query.error.message : "Failed to load RFQ"}
        </p>
        <Link to="/admin" className="text-sm text-teal-700 hover:underline">
          ← Back to list
        </Link>
      </div>
    );
  }
  const rfq = query.data;

  const allowed = nextAllowed(rfq.status);

  return (
    <div className="space-y-3">
      <Link to="/admin" className="text-sm text-teal-700 hover:underline">
        ← Back to list
      </Link>

      <AdminCard>
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold text-slate-900">
            {serviceCategoryLabels[rfq.serviceCategory]}
          </h2>
          <StatusBadge status={rfq.status} />
        </div>
        <dl className="mt-2 grid grid-cols-1 gap-2 text-sm">
          <Row label="Urgency" value={urgencyLabels[rfq.urgency]} />
          <Row
            label="Location"
            value={`${rfq.location.street}${rfq.location.unit ? `, ${rfq.location.unit}` : ""}, ${rfq.location.city}, ${rfq.location.state} ${rfq.location.postalCode}`}
          />
          <Row label="Customer (sub)" value={rfq.customerSub} mono />
          <Row label="RFQ id" value={rfq.id} mono />
          <Row label="Submitted" value={new Date(rfq.createdAt).toLocaleString()} />
          <Row label="Last update" value={new Date(rfq.updatedAt).toLocaleString()} />
        </dl>
        <p className="mt-3 whitespace-pre-wrap text-sm text-slate-800">{rfq.description}</p>
      </AdminCard>

      <CustomerContactCard customer={rfq.customer} customerSub={rfq.customerSub} />

      {rfq.images && rfq.images.length > 0 && (
        <AdminCard>
          <h3 className="text-sm font-medium text-slate-900">Photos ({rfq.images.length})</h3>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {rfq.images.map((img) => (
              <button
                key={img.id}
                type="button"
                onClick={() => setLightbox(img.url)}
                className="aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-100"
              >
                <img src={img.url} alt="" className="h-full w-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </AdminCard>
      )}

      <AdminCard>
        <h3 className="text-sm font-medium text-slate-900">Change status</h3>
        {allowed.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">
            No further admin transitions are allowed from{" "}
            <span className="font-medium">{rfq.status}</span>.
          </p>
        ) : (
          <div className="mt-2 flex flex-col gap-2">
            {allowed.map((next) => {
              const tab = ASSIGNABLE.find((a) => a.value === next);
              if (!tab) return null;
              return (
                <button
                  key={next}
                  type="button"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate(next)}
                  className="min-h-[44px] rounded-md bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50"
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}
        {mutation.isError && (
          <p className="mt-2 text-sm text-rose-700">
            {mutation.error instanceof Error ? mutation.error.message : "Update failed"}
          </p>
        )}
      </AdminCard>

      <AdminCard>
        <h3 className="text-sm font-medium text-slate-900">
          Notes ({rfq.adminNotes?.length ?? 0})
        </h3>
        {rfq.adminNotes && rfq.adminNotes.length > 0 ? (
          <ul className="mt-2 space-y-2">
            {rfq.adminNotes.map((note) => (
              <NoteRow key={note.id} note={note} />
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-slate-500">No notes yet.</p>
        )}
        <form
          className="mt-3 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            const text = noteText.trim();
            if (!text) return;
            addNote.mutate(text, {
              onSuccess: () => setNoteText(""),
            });
          }}
        >
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Add an internal note (visible to admins only)…"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-slate-500">{noteText.length}/2000</span>
            <button
              type="submit"
              disabled={addNote.isPending || noteText.trim().length === 0}
              className="min-h-[40px] rounded-md bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50"
            >
              {addNote.isPending ? "Saving…" : "Add note"}
            </button>
          </div>
          {addNote.isError && (
            <p className="text-sm text-rose-700">
              {addNote.error instanceof Error ? addNote.error.message : "Could not save note"}
            </p>
          )}
        </form>
      </AdminCard>

      {lightbox && (
        <button
          type="button"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          aria-label="Close image"
        >
          <img src={lightbox} alt="" className="max-h-full max-w-full object-contain" />
        </button>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`text-sm text-slate-800 ${mono ? "font-mono break-all" : ""}`}>{value}</dd>
    </div>
  );
}

function CustomerContactCard({
  customer,
  customerSub,
}: {
  customer?: AdminCustomer;
  customerSub: string;
}) {
  const name =
    customer && (customer.givenName || customer.familyName)
      ? [customer.givenName, customer.familyName].filter(Boolean).join(" ")
      : null;
  return (
    <AdminCard>
      <h3 className="text-sm font-medium text-slate-900">Customer contact</h3>
      {!customer ? (
        <p className="mt-2 text-sm text-slate-500">
          Profile not found for sub <span className="font-mono">{customerSub}</span>.
        </p>
      ) : (
        <dl className="mt-2 grid grid-cols-1 gap-2 text-sm">
          {name && <Row label="Name" value={name} />}
          {customer.email && (
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-slate-500">Email</dt>
              <dd className="text-sm">
                <a href={`mailto:${customer.email}`} className="text-teal-700 hover:underline">
                  {customer.email}
                </a>
              </dd>
            </div>
          )}
          {customer.phoneNumber && (
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-slate-500">Phone</dt>
              <dd className="text-sm">
                <a href={`tel:${customer.phoneNumber}`} className="text-teal-700 hover:underline">
                  {customer.phoneNumber}
                </a>
              </dd>
            </div>
          )}
          {!name && !customer.email && !customer.phoneNumber && (
            <p className="text-sm text-slate-500">No contact details on file.</p>
          )}
        </dl>
      )}
    </AdminCard>
  );
}

function NoteRow({ note }: { note: AdminNote }) {
  const authorShort = `${note.authorSub.slice(0, 8)}…`;
  const when = new Date(note.createdAt).toLocaleString();
  return (
    <li className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
        <span className="font-mono">{authorShort}</span>
        <span>{when}</span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{note.text}</p>
    </li>
  );
}

function nextAllowed(status: string): AdminAssignableStatus[] {
  switch (status) {
    case "SUBMITTED":
      return ["IN_REVIEW", "CLOSED"];
    case "IN_REVIEW":
      return ["CLOSED"];
    default:
      return [];
  }
}
